/**
 * Oct 20 (FD-06 / FD-07) — "Picked up — work incomplete": repairs whose vehicle left before the work was done,
 * kept in front of the front office so the outstanding work never disappears from follow-up. Shows the stated
 * reason and, when one was given, the expected return date — informational, not an appointment.
 *
 * Oct 20 addendum (L6 / L3): an outstanding repair is either still with the customer ("Vehicle returned" checks it
 * back in) or returned and awaiting scheduling ("Schedule remaining work" creates the linked continuation). Returned,
 * unscheduled work surfaces here — it is not a stale-work alert.
 */
import { useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import type { OutstandingIncompletePickup } from '@/hooks/foh/useOutstandingIncompletePickups';

export interface OutstandingIncompletePickupsProps {
  rows: readonly OutstandingIncompletePickup[];
  loading: boolean;
  shopTimezone: string;
  names: Readonly<Record<string, string>>;
  onOpen: (ticketId: string) => void;
  /** Oct 20 addendum: the front-office acts, offered only when given. */
  onCheckIn?: (row: OutstandingIncompletePickup) => void;
  onSchedule?: (row: OutstandingIncompletePickup) => void;
}

function day(iso: string, tz: string): string {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? formatInTimeZone(d, tz, 'EEE MMM d') : iso;
}

/** "Expected back Fri Oct 23" — a date-only value shown as written, never shifted by a time zone. */
export function expectedBackWords(date: string | null): string {
  if (!date) return 'No return date given';
  const d = new Date(`${date}T12:00:00Z`);
  return Number.isFinite(d.getTime()) ? `Expected back ${formatInTimeZone(d, 'UTC', 'EEE MMM d')}` : `Expected back ${date}`;
}

export function OutstandingIncompletePickups({ rows, loading, shopTimezone, names, onOpen, onCheckIn, onSchedule }: OutstandingIncompletePickupsProps) {
  const [open, setOpen] = useState(true);
  if (loading || rows.length === 0) return null;
  return (
    <section data-testid="foh-outstanding-incomplete-pickups" className="space-y-2">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-center gap-1.5 text-sm font-semibold">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Picked up — work incomplete
        <span className="rounded-full bg-muted px-2 text-xs font-medium" data-testid="foh-outstanding-incomplete-count">{rows.length}</span>
      </button>
      {open && (
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => (
            <li key={r.ticketId} className="rounded-lg border bg-card p-3 text-sm space-y-1" data-testid={`foh-outstanding-${r.ticketId}`}>
              <button type="button" className="flex items-center gap-1.5 font-semibold text-left" onClick={() => onOpen(r.ticketId)}>
                {r.ticketNumber != null ? `RO #${r.ticketNumber}` : 'Repair order'}
                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              {(r.vehicle || r.customerName) && (
                <div className="text-muted-foreground truncate">{[r.customerName, r.vehicle].filter(Boolean).join(' · ')}</div>
              )}
              <div data-testid={`foh-outstanding-reason-${r.ticketId}`}>{r.reason}</div>
              <div className="text-xs text-muted-foreground">
                Left {day(r.pickedUpAt, shopTimezone)}{names[r.recordedBy] ? ` · recorded by ${names[r.recordedBy]}` : ''}
              </div>
              <div className="text-xs font-medium" data-testid={`foh-outstanding-return-${r.ticketId}`}>
                {expectedBackWords(r.expectedReturnDate)}
                <span className="font-normal text-muted-foreground"> (not an appointment)</span>
              </div>
              {r.state === 'returned_awaiting_scheduling' && (
                <div className="text-xs font-semibold text-primary" data-testid={`foh-outstanding-state-${r.ticketId}`}>
                  Returned {r.returnedAt ? day(r.returnedAt, shopTimezone) : ''} — awaiting scheduling
                </div>
              )}
              {r.state === 'awaiting_return' && onCheckIn && (
                <button type="button" className="min-h-11 rounded-md border px-3 text-sm font-medium hover:bg-muted"
                        data-testid={`foh-outstanding-check-in-${r.ticketId}`} onClick={() => onCheckIn(r)}>
                  Vehicle returned — check in
                </button>
              )}
              {r.state === 'returned_awaiting_scheduling' && onSchedule && (
                <button type="button" className="min-h-11 rounded-md border px-3 text-sm font-medium hover:bg-muted"
                        data-testid={`foh-outstanding-schedule-${r.ticketId}`} onClick={() => onSchedule(r)}>
                  Schedule remaining work
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
